import React, { ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { T, TF } from "react-translator-component"
import { styles } from "../styles";
import GroupIcon from "@mui/icons-material/Group";
import CurrencyYenSharpIcon from "@mui/icons-material/CurrencyYenSharp";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import SupervisedUserCircleOutlinedIcon from "@mui/icons-material/SupervisedUserCircleOutlined";
import ButtonArrow from "../../components/GreenCity/ButtonArrow";
import GCBackButton from "../../components/GreenCity/GCBackButton";
import LoadingModal from "../../components/LoadingModal";
import MessageModal from "../../components/Models/GreenCity/MessageModal";
import { colors } from "../../common/colors";
import { Box, Typography } from "@mui/material";
import { setGroupInfo } from "../../redux/Reducers/gcGroupReducer";
import InsertCommentIcon from "@mui/icons-material/InsertComment";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { getRole, isMemberRole, isFinanceRole, isGroupLeader, isOver18YearsOld, isPrivateGroup } from "../../util/utils";
import { ErrorMessageModal } from "../../components/ErrorMessageModal";
import appServices from "../../services/app-services/appServices";

function GroupOwnerSettingPage(): ReactElement {
  const [cancelRequests, setCancelRequests] = React.useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { groupId } = location.state || {}; // Destructure your props
  const [groupIcon, setGroupIcon] = useState<File>();
  const { user } = useSelector((state: any) => state.userAuth);
  const [accountInfo, setAccountInfo] = useState<any>();
  const [groupInfo, setTargetGroupInfo] = useState<any>();
  const [userGroupRole, setUserGroupRole] = useState<string>('');
  const [isAdultOwner, setIsAdultOwner] = useState<boolean>(false);
  const [isShowNoFeeDialog, setIsShowNoFeeDialog] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string[]>([]);

  // グループ脱退時のエラーメッセージ
  const groupCancelErrorMessage: string[] = [
    T('group_cancel_membership_onself_error_1'),
    '　',
    T('group_cancel_membership_onself_error_2'),
    T('group_cancel_membership_onself_error_3'),
    T('group_cancel_membership_onself_error_4')
  ]
  // グループを閉じる際のエラーメッセージ
  const groupCloseErrorMessage: string[] = [
    T('group_close_error_1'),
    '　',
    T('group_close_error_2'),
    T('group_close_error_3'),
    T('group_close_error_4'),
    T('group_close_error_5')
  ]

  const getCancelRequests = async () => {
    try {
      const res = await appServices.getCancelRequestsOfGroup(groupInfo?.id);
      setCancelRequests(res?.post_event_users);
    } catch (error) {
      console.error(error);
    }
  };

  const getGroupBalance = async () => {
    try {
      const res = await appServices.getGroupBalance(groupInfo?.id);
      setAccountInfo(res?.wallet_information?.balance?.money?.amount);
    } catch (error) {
      console.error(error);
    }
  };

  const getGroupInfo = (icon?: File, banner?: File) => {
    return {
      id: groupInfo.id,
      groupName: groupInfo.name,
      groupIsPublic: groupInfo?.group_type,
      groupImage: icon,
      groupMainImage: banner,
      groupDesc: groupInfo?.description, // From state
      groupAnnouncements: groupInfo?.member_group_activities, // From state
      groupWebURLs: groupInfo?.social_links, // You can replace this with the actual value
      groupApprovalRequired: groupInfo?.approval_required,
      piiRequired: groupInfo?.personal_information_needed ? "YES" : "NO",
      groupIsLimited: true,
      groupMemberShipFeeAmount: groupInfo?.joining_fee_amount,
      isMonthly: groupInfo?.fee_type === "monthly", // Static value
      groupPaymentField: groupInfo?.fee_amount,
      groupPaymentDescription: groupInfo?.fee_explanation,
      user: groupInfo.owner,
      owner: groupInfo.owner,
      role: userGroupRole,
      memberGroupUsers: groupInfo?.member_group_users,
      posts_count:groupInfo?.posts_count,
      isPaid: groupInfo?.is_paid
    };
  };

  const navigateGroupTop = () => {
    navigate("/groupinforeview", {state: {groupId: groupInfo?.id}});
  };

  const invite = () => {
    // Dispatch action to save group details
    dispatch(setGroupInfo(getGroupInfo(groupIcon)));
    navigate("/invitation");
  };

  const [isShowErrorModal, setIsShowErrorModal] = useState(false);
  const handleDeleteMembership = async () => {
    setLoading(true);
    try {
      let res = await appServices.findMember(user.id, groupInfo?.id);
      res = await appServices.canDeleteMembership(res?.member_group_user?.id)
      if (res?.can_delete) {
        dispatch(setGroupInfo(getGroupInfo(groupIcon)));
        navigate("/groupcancelmembership")
      } else {
        setErrorMessage(groupCancelErrorMessage)
        setIsShowErrorModal(true)
      }
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message ?? [T("api_unknown_error")])
      setIsShowErrorModal(true)
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteMemberGroup = async () => {
    setLoading(true);
    try {
      const res = await appServices.canCloseMemberGroup(groupInfo?.id);
      if (res?.can_close) {
        dispatch(setGroupInfo(getGroupInfo(groupIcon)));
        navigate("/groupclosedetails");
      } else {
        setErrorMessage(groupCloseErrorMessage)
        setIsShowErrorModal(true);
      }
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message ?? [T("api_unknown_error")])
      setIsShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleMemberList = () => {
    dispatch(setGroupInfo(getGroupInfo(groupIcon)));
    navigate("/memberlist");
  };

  const handleClickPaymentStatus = () => {
    if (groupInfo?.fee_amount > 0) {
      navigateToGroupMemberFee()
    } else {
      showNoFeeDialog()
    }
  }

  const navigateToViewGroup = (post_type: "public" | "private" | "private_users") => {
    dispatch(setGroupInfo(getGroupInfo(groupIcon)));
    navigate("/viewGroupPost", {
      state: {
        post_type: post_type,
      },
    });
  };

  const navigateToGroupCalendar = () => {
    dispatch(setGroupInfo(getGroupInfo(groupIcon)));
    navigate("/groupeventscalendar");
  };

  const navigateToCancelRequests = () => {
    dispatch(setGroupInfo(getGroupInfo(groupIcon)));
    navigate("/cancelrequests");
  };

  const navigateToGroupEventSetting = () => {
    dispatch(setGroupInfo(getGroupInfo(groupIcon)));
    navigate("/groupeventsetting");
  };

  const navigateToGroupMemberFee = () => {
    dispatch(setGroupInfo(getGroupInfo(groupIcon)));
    navigate("/memberPaymentStatus");
  };

  const navigateToGroupPaymentHistory = () => {
    dispatch(setGroupInfo(getGroupInfo(groupIcon)));
    navigate("/paymenthistory", { state: {groupId: groupInfo.id} });
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    appServices.getMembersGroup(groupId).then((res) => {
      const group = res.member_group
      setTargetGroupInfo(group)

      let role
      if (group.role) {
        role = group.role
      } else {
        role = isGroupLeader(group, user) ? 'leader' : ''
      }
      setUserGroupRole(role)
    }).catch((error) => {
      console.error(error)
    });
  }, []);

  useEffect(() => {
    if (!groupInfo) return

    const isGroupLeaderOver18 = isOver18YearsOld(groupInfo.owner.dob)
    setIsAdultOwner(isGroupLeaderOver18)

    // グループのリーダーが18歳未満ならお支払い関係がdisabledなのでAPIを呼ばない
    if (isGroupLeaderOver18) {
      getCancelRequests();
      getGroupBalance();
    }

    appServices.getGroupIcon(groupInfo.id).then(blob => {
      const file = new File([blob], groupInfo.group_icon, { type: blob.type });
      setGroupIcon(file);
    }).catch(() => {
      console.error("Failed to get group icon image")
    });
  }, [groupInfo]);

  const navigatePaytoStoreQR = () => {
    dispatch(setGroupInfo(getGroupInfo(groupIcon)));
    navigate("/paytostoreqr");
  };

  const navigatePaytoUser = () => {
    dispatch(setGroupInfo(getGroupInfo(groupIcon)));
    navigate("/paytouser");
  };
  const groupReimbursements = () => {
    dispatch(setGroupInfo(getGroupInfo(groupIcon)));
    navigate("/groupreimbursements");
  };

  const showNoFeeDialog = () => {
    setIsShowNoFeeDialog(true)
    setMessage(
      T("group_page_no_fee")
    )
  }


  return (
    <div style={{ paddingTop: 100, marginLeft: 16 }}>
      <GCBackButton onClick={() => navigate("/myfarm")} />
      <Box style={{ padding: 16 }}>
        <Typography style={{ fontWeight: "700" }}>
          <img
            src={groupIcon ? URL.createObjectURL(groupIcon) : ''}
            alt={"user_img"}
            style={{
              ...styles.farmProfileIcon,
              height: 50,
              width: 50,
              margin: 0,
            }}
          />
          &nbsp;{groupInfo?.name}
        </Typography>
        <Typography style={{ fontSize: 10, textAlign: "center" }}>
          {TF(T("role_notice"), getRole(userGroupRole))}
        </Typography>
      </Box>

      <div style={styles.portionBg}>
        <p style={{ fontSize: 14, fontWeight: 600 }}>
          <SupervisedUserCircleOutlinedIcon /> {T("group_page_member_group")}
        </p>

        <ButtonArrow
          title={T("group_page_group_top")}
          onClick={() => navigateGroupTop()}
        />
      </div>

      <div style={styles.portionBg}>
        <p style={{ fontSize: 14, fontWeight: 600 }}>
          <BorderColorOutlinedIcon /> {T("post")}
        </p>

        <ButtonArrow disabled={isPrivateGroup(groupInfo?.group_type)} title={T("everyone_post")} onClick={() => navigateToViewGroup("public")} />

        <ButtonArrow title={T("event_post")} onClick={navigateToGroupEventSetting} />

        <ButtonArrow title={T("inside_group_post")} onClick={() => navigateToViewGroup("private")} />

        <ButtonArrow
          title={T("group_private_post")}
          onClick={() => navigateToViewGroup("private_users")}
        />
      </div>
      <div style={styles.portionBg}>
        <p style={{ fontSize: 14, fontWeight: 600 }}>
          <GroupIcon /> {T("group_page_member")}
        </p>

        <ButtonArrow
          title={T("member_list")}
          onClick={() => {
            handleMemberList();
          }}
        />

        <ButtonArrow
          disabled={isMemberRole(userGroupRole) || isFinanceRole(userGroupRole)}
          title={T("group_page_member_invite")}
          onClick={() => {
            invite();
          }}
        />
      </div>

      <div style={styles.portionBg}>
        <p style={{ fontSize: 14, fontWeight: 600 }}>
          <CurrencyYenSharpIcon
            style={{
              borderWidth: 1,
              borderStyle: "solid",
              width: 26,
              height: 26,
              borderRadius: 13,
              padding: 2,
              margin: 2,
            }}
          />
          {T("payment")}
        </p>
        {isAdultOwner && !isMemberRole(groupInfo?.role) && (
          <Typography style={{ fontSize: 10, textAlign: "center" }}>
            {TF(T("group_page_balance"), accountInfo ?? "")}
          </Typography>
        )}

        <ButtonArrow
          disabled={!isAdultOwner || isMemberRole(userGroupRole)}
          title={T("group_page_pay_to_member")}
          onClick={() => {
            navigatePaytoUser();
          }}
        />
        <ButtonArrow
          disabled={!isAdultOwner || isMemberRole(userGroupRole)}
          title={T("group_page_pay_to_shop")}
          onClick={() => {
            navigatePaytoStoreQR();
          }}
        />
        <ButtonArrow
          disabled={!isAdultOwner || isMemberRole(userGroupRole)}
          title={T("reimbursement")}
          onClick={() => {
            groupReimbursements();
          }}
        />
        <ButtonArrow
          disabled={!isAdultOwner || isMemberRole(userGroupRole)}
          title={T("payment_status")}
          onClick={() => {handleClickPaymentStatus()}}
        />
        <ButtonArrow
          disabled={!isAdultOwner || isMemberRole(userGroupRole)}
          title={T("event_cancel_settlement")}
          onClick={() => {
            navigateToCancelRequests();
          }}
          badge={cancelRequests}
        />
        <ButtonArrow disabled={!isAdultOwner || isMemberRole(userGroupRole)} title={T("payment_history")} onClick={navigateToGroupPaymentHistory} />
      </div>

      <div style={styles.portionBg}>
        <p style={{ fontSize: 14, fontWeight: 600 }}>
          <InsertCommentIcon />
          {T("chat")}
        </p>

        <ButtonArrow
          title={T("chat_list")}
          disabled={isMemberRole(userGroupRole)}
          onClick={() => {
            navigate("/group_chat_list", {
              state: {
                groupInfo: groupInfo,
              },
            });
          }}
        />
      </div>

      <div style={styles.portionBg}>
        <p style={{ fontSize: 14, fontWeight: 600 }}>
          <CalendarMonthIcon
            style={{
              borderWidth: 1,
              width: 26,
              height: 26,
              padding: 2,
              margin: 2,
            }}
          />
          {T("group_page_calendar")}
        </p>

        <ButtonArrow title={T("group_page_event_calendar")} onClick={navigateToGroupCalendar} />
      </div>

      <div style={styles.portionBg}>
        <p style={{ fontSize: 14, fontWeight: 600 }}>
          <SettingsOutlinedIcon
            style={{
              borderWidth: 1,
              width: 26,
              height: 26,
              padding: 2,
              margin: 2,
            }}
          />
          {T("group_page_other")}
        </p>

        <ButtonArrow title={T("close_group")} disabled={!isGroupLeader(groupInfo, user)} onClick={handleDeleteMemberGroup}/>
        <ButtonArrow title={T("group_page_drop_out_group")} disabled={isGroupLeader(groupInfo, user)} onClick={handleDeleteMembership} />
      </div>

      {/* グループ脱退時のエラーダイアログ */}
      <ErrorMessageModal
        open={isShowErrorModal}
        messages={errorMessage}
        onClose={() => setIsShowErrorModal(false)}
        alignLeft={errorMessage.length > 1}
      />

      <MessageModal
        open={isShowNoFeeDialog}
        onContinue={() => {}}
        message={message}
        onClose={() => {setIsShowNoFeeDialog(false)}}
        showButton
        buttonText={T('close')}
        buttonStyles={style.errorDialogButton}
      />
      <LoadingModal visible={loading} />
    </div>
  );
}

const style = {
  messageModal:{
    textAlign: "center"
  },
  errorDialogButton: {
    width: "50%",
    minWitdh: "100px",
    alignSelf: "center",
    display: "flex",
    border: "3px solid",
    borderRadius: "10px",
    borderColor: colors.farm_blue200,
    color: colors.farm_blue200
  }
}

export default GroupOwnerSettingPage;
